/**
 * Layout Context
 *
 * Provides layout configuration state that pages can set and TobBar can consume.
 * This decouples TobBar from route-specific logic.
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { LayoutConfig } from '../types/layout';

interface LayoutContextType {
  layoutConfig: LayoutConfig;
  setLayoutConfig: (config: LayoutConfig) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

interface LayoutProviderProps {
  children: ReactNode;
}

export function LayoutProvider({ children }: LayoutProviderProps) {
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
    showBackButton: false,
    backButtonText: 'Back',
  });

  return (
    <LayoutContext.Provider value={{ layoutConfig, setLayoutConfig }}>
      {children}
    </LayoutContext.Provider>
  );
}

/**
 * Hook to access layout configuration
 * Use this in TobBar to read the current layout config
 */
export function useLayoutConfig() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayoutConfig must be used within a LayoutProvider');
  }
  return context;
}
